import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import Draggable from "react-draggable";
import axios from "../../../api/axios";
import toast from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";

// Configure PDF.js worker - use dynamic version from pdfjs to match exactly
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
import {
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  CircleStackIcon,
  Square3Stack3DIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../components/utils/PMDialog";
import { PMField } from "../../../components/utils/PMField";

export const DrawingViewer = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const pdfContainerRef = useRef(null);

  const [drawing, setDrawing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfScale, setPdfScale] = useState(1.0);
  const [pdfPosition, setPdfPosition] = useState({ x: 0, y: 0 });
  const [pdfBaseSize, setPdfBaseSize] = useState({ width: 0, height: 0 });
  const [pdfScaledSize, setPdfScaledSize] = useState({ width: 0, height: 0 });
  const [modules, setModules] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleConfigDialog, setModuleConfigDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({}); // Track unsaved module positions

  useEffect(() => {
    loadDrawing();
  }, [id]);

  // Calculate scaled PDF size when base size or scale changes
  useEffect(() => {
    if (pdfBaseSize.width > 0 && pdfBaseSize.height > 0) {
      const scaledWidth = pdfBaseSize.width * pdfScale;
      const scaledHeight = pdfBaseSize.height * pdfScale;
      setPdfScaledSize({ width: scaledWidth, height: scaledHeight });
    }
  }, [pdfBaseSize, pdfScale]);

  // Center PDF when it loads or scale changes
  useEffect(() => {
    if (pdfScaledSize.width > 0 && pdfScaledSize.height > 0 && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      // Center the PDF
      const centerX = (containerWidth - pdfScaledSize.width) / 2;
      const centerY = (containerHeight - pdfScaledSize.height) / 2;
      
      setPdfPosition({ x: centerX, y: centerY });
    }
  }, [pdfScaledSize]);

  // Validate and clamp module positions when PDF dimensions are available
  useEffect(() => {
    if (pdfBaseSize.width > 0 && pdfBaseSize.height > 0 && modules.length > 0) {
      setModules((prevModules) =>
        prevModules.map((module) => {
          const moduleWidth = module.width || 50;
          const moduleHeight = module.height || 50;
          let xPos = module.x_position || 0;
          let yPos = module.y_position || 0;

          // Clamp to PDF bounds
          xPos = Math.max(0, Math.min(pdfBaseSize.width - moduleWidth, xPos));
          yPos = Math.max(0, Math.min(pdfBaseSize.height - moduleHeight, yPos));

          // Only update if position changed
          if (xPos !== module.x_position || yPos !== module.y_position) {
            return { ...module, x_position: xPos, y_position: yPos };
          }
          return module;
        })
      );
    }
  }, [pdfBaseSize]);

  const loadDrawing = async () => {
    try {
      setLoading(true);
      const response = await axios({
        method: "get",
        url: `/drawings/${id}`,
      });
      const drawingData = response.data;
      setDrawing(drawingData);
      // Ensure module positions are numbers and validate them
      // Note: We'll validate against PDF bounds after PDF loads
      const modulesWithNumbers = (drawingData.drawing_modules || []).map(module => ({
        ...module,
        x_position: parseFloat(module.x_position) || 0,
        y_position: parseFloat(module.y_position) || 0,
        width: parseFloat(module.width) || 50,
        height: parseFloat(module.height) || 50,
      }));
      setModules(modulesWithNumbers);

      // Try to get PDF URL from document
      let pdfFileUrl = null;
      
      console.log("Drawing data:", drawingData);
      console.log("Document data:", drawingData.document);
      
      if (drawingData.document?.file?.url) {
        // File URL is already in the response
        pdfFileUrl = drawingData.document.file.url;
        console.log("Using file URL from response:", pdfFileUrl);
      } else if (drawingData.document?.id) {
        // Fetch document separately to get file URL
        console.log("Fetching document separately, ID:", drawingData.document.id);
        try {
          const docResponse = await axios({
            method: "get",
            url: `/documents/${drawingData.document.id}`,
            params: { fields: "id,title,file" },
          });
          console.log("Document response:", docResponse.data);
          if (docResponse.data?.file?.url) {
            pdfFileUrl = docResponse.data.file.url;
            console.log("Got file URL from separate fetch:", pdfFileUrl);
          } else if (docResponse.data?.file) {
            // Check if file object has different structure
            console.log("File object structure:", docResponse.data.file);
            pdfFileUrl = docResponse.data.file.url || docResponse.data.file.full_url;
          }
        } catch (err) {
          console.error("Error fetching document file:", err);
          toast.error(t("drawings.load_error"));
        }
      }

      // Load PDF if we have a URL
      if (pdfFileUrl) {
        try {
          console.log("Loading PDF from URL:", pdfFileUrl);
          
          // Check if URL is absolute (external) or relative (needs auth)
          const isAbsoluteUrl = pdfFileUrl.startsWith('http://') || pdfFileUrl.startsWith('https://');
          
          if (isAbsoluteUrl) {
            // For absolute URLs (e.g., S3), use fetch directly
            const res = await fetch(pdfFileUrl);
            if (!res.ok) {
              throw new Error(`Failed to fetch PDF: ${res.status} ${res.statusText}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
            console.log("PDF loaded successfully from absolute URL");
          } else {
            // For relative URLs, use axios to include authentication headers
            const res = await axios({
              method: "get",
              url: pdfFileUrl,
              responseType: "blob",
            });
            
            const blob = res.data;
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
            console.log("PDF loaded successfully from relative URL with auth");
          }
        } catch (err) {
          console.error("Error loading PDF blob:", err);
          console.error("Error details:", err.response?.status, err.response?.statusText);
          setPdfError(err.message || "Failed to load PDF file");
          toast.error(t("drawings.load_error"));
        }
      } else {
        console.warn("No PDF file URL found for document");
        setPdfError("No PDF file URL found for document");
        toast.error(t("drawings.load_error"));
      }
    } catch (err) {
      console.error("Error loading drawing:", err);
      toast.error(t("drawings.load_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    // Store both container-relative and client coordinates
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContextMenu({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        clientX: e.clientX,
        clientY: e.clientY,
      });
    }
  };

  const handleAddModule = async (type) => {
    if (!contextMenu) return;

    // Calculate position relative to PDF container (accounting for scale and position)
    const pdfRect = pdfContainerRef.current?.getBoundingClientRect();
    
    if (!pdfRect) return;

    // Use client coordinates for accurate positioning
    const clientX = contextMenu.clientX;
    const clientY = contextMenu.clientY;
    
    // Calculate position relative to PDF container origin (top-left of PDF)
    // pdfRect already accounts for pdfPosition (from Draggable) and scale (from Page component)
    // So we need to convert from screen coordinates to PDF coordinates
    // The PDF is scaled by pdfScale, so we divide by pdfScale to get the original PDF coordinates
    const x = (clientX - pdfRect.left) / pdfScale;
    const y = (clientY - pdfRect.top) / pdfScale;

    // Default module dimensions
    const moduleWidth = type === "rectangular" ? 100 : 50;
    const moduleHeight = type === "rectangular" ? 50 : 50;

    // Ensure positions are numbers and within PDF bounds
    let xPos = Math.max(0, parseFloat(x) || 0);
    let yPos = Math.max(0, parseFloat(y) || 0);

    // Clamp to PDF bounds to prevent modules from being placed outside
    if (pdfBaseSize.width > 0 && pdfBaseSize.height > 0) {
      xPos = Math.min(pdfBaseSize.width - moduleWidth, xPos);
      yPos = Math.min(pdfBaseSize.height - moduleHeight, yPos);
    }

    const moduleData = {
      drawing_module: {
        module_type: type,
        x_position: xPos,
        y_position: yPos,
        width: type === "rectangular" ? 100 : 50,
        height: type === "rectangular" ? 50 : 50,
        color: "#000000",
        filled: false,
      },
    };

    try {
      const response = await axios({
        method: "post",
        url: `/drawings/${id}/drawing_modules`,
        data: moduleData,
      });
      // Ensure response data has numeric positions
      const newModule = {
        ...response.data,
        x_position: parseFloat(response.data.x_position) || 0,
        y_position: parseFloat(response.data.y_position) || 0,
        width: parseFloat(response.data.width) || (type === "rectangular" ? 100 : 50),
        height: parseFloat(response.data.height) || (type === "rectangular" ? 50 : 50),
      };
      setModules((prev) => [...prev, newModule]);
      toast.success(t("drawings.module.add") + " " + t("drawings.module." + type));
    } catch (err) {
      console.error("Error adding module:", err);
      toast.error(t("drawings.create_error"));
    }

    setContextMenu(null);
  };

  const handleModuleMove = (moduleId, newPosition) => {
    // newPosition is the position from Draggable, which is already scaled
    // We need to convert it back to the original PDF coordinates
    let xPos = parseFloat(newPosition.x) / pdfScale || 0;
    let yPos = parseFloat(newPosition.y) / pdfScale || 0;

    // Get module dimensions for bounds checking
    const module = modules.find(m => m.id === moduleId);
    const moduleWidth = (module?.width || 50) / pdfScale;
    const moduleHeight = (module?.height || 50) / pdfScale;

    // Clamp to PDF bounds to prevent modules from going outside
    if (pdfBaseSize.width > 0 && pdfBaseSize.height > 0) {
      xPos = Math.max(0, Math.min(pdfBaseSize.width - moduleWidth, xPos));
      yPos = Math.max(0, Math.min(pdfBaseSize.height - moduleHeight, yPos));
    }

    // Update local state immediately for smooth dragging
    setModules((prev) =>
      prev.map((m) =>
        m.id === moduleId
          ? { ...m, x_position: xPos, y_position: yPos }
          : m
      )
    );

    // Store pending changes instead of saving immediately
    setPendingChanges((prev) => ({
      ...prev,
      [moduleId]: { x_position: xPos, y_position: yPos },
    }));
  };

  const handleSaveAllChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.info(t("drawings.no_changes"));
      return;
    }

    try {
      setSaving(true);
      const updatePromises = Object.entries(pendingChanges).map(
        ([moduleId, changes]) =>
          axios({
            method: "put",
            url: `/drawing_modules/${moduleId}`,
            data: {
              drawing_module: changes,
            },
          })
      );

      await Promise.all(updatePromises);
      setPendingChanges({});
      toast.success(t("drawings.updated"));
    } catch (err) {
      console.error("Error saving changes:", err);
      toast.error(t("drawings.update_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm(t("drawings.module.delete_confirm"))) {
      return;
    }

    try {
      await axios({
        method: "delete",
        url: `/drawing_modules/${moduleId}`,
      });
      setModules((prev) => prev.filter((m) => m.id !== moduleId));
      toast.success(t("drawings.module.delete"));
    } catch (err) {
      console.error("Error deleting module:", err);
      toast.error(t("drawings.delete_error"));
    }
  };

  const handleModuleConfig = (module) => {
    if (module.module_type === "rectangular") {
      // Use pending changes if available, otherwise use current module state
      const moduleWithPendingChanges = pendingChanges[module.id]
        ? { ...module, ...pendingChanges[module.id] }
        : module;
      setModuleConfigDialog(moduleWithPendingChanges);
    }
  };

  const handleSaveModuleConfig = async () => {
    if (!moduleConfigDialog) return;

    try {
      setSaving(true);
      // Include pending position changes if they exist
      const pendingPosition = pendingChanges[moduleConfigDialog.id];
      const updateData = {
        color: moduleConfigDialog.color,
        filled: moduleConfigDialog.filled,
      };
      
      if (pendingPosition) {
        updateData.x_position = pendingPosition.x_position;
        updateData.y_position = pendingPosition.y_position;
      }

      const response = await axios({
        method: "put",
        url: `/drawing_modules/${moduleConfigDialog.id}`,
        data: {
          drawing_module: updateData,
        },
      });
      
      // Update modules state
      setModules((prev) =>
        prev.map((m) => (m.id === moduleConfigDialog.id ? response.data : m))
      );
      
      // Remove from pending changes if position was included
      if (pendingPosition) {
        setPendingChanges((prev) => {
          const newPending = { ...prev };
          delete newPending[moduleConfigDialog.id];
          return newPending;
        });
      }
      
      setModuleConfigDialog(null);
      toast.success(t("drawings.updated"));
    } catch (err) {
      console.error("Error updating module:", err);
      toast.error(t("drawings.update_error"));
    } finally {
      setSaving(false);
    }
  };

  const renderModule = (module) => {
    // Use pending changes if available, otherwise use current module state
    const moduleToRender = pendingChanges[module.id]
      ? { ...module, ...pendingChanges[module.id] }
      : module;

    // Get module dimensions
    const moduleWidth = moduleToRender.width || 50;
    const moduleHeight = moduleToRender.height || 50;

    // Calculate position relative to PDF container with scale
    let scaledX = (moduleToRender.x_position || 0) * pdfScale;
    let scaledY = (moduleToRender.y_position || 0) * pdfScale;

    // Clamp positions to ensure module stays within PDF bounds
    if (pdfScaledSize.width > 0 && pdfScaledSize.height > 0) {
      const scaledModuleWidth = moduleWidth * pdfScale;
      const scaledModuleHeight = moduleHeight * pdfScale;
      scaledX = Math.max(0, Math.min(pdfScaledSize.width - scaledModuleWidth, scaledX));
      scaledY = Math.max(0, Math.min(pdfScaledSize.height - scaledModuleHeight, scaledY));
    }

    const style = {
      position: "absolute",
      cursor: "move",
      zIndex: selectedModule?.id === module.id ? 1000 : 999,
    };

    let content;
    switch (moduleToRender.module_type) {
      case "arrow":
        content = (
          <svg
            width={moduleToRender.width || 50}
            height={moduleToRender.height || 50}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transform: "rotate(-45deg)" }}
          >
            <path
              d="M12 2L2 12L12 22M2 12H22"
              stroke={moduleToRender.color || "#000000"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={moduleToRender.filled ? moduleToRender.color || "#000000" : "none"}
            />
          </svg>
        );
        break;
      case "circle":
        content = (
          <div
            style={{
              width: moduleToRender.width || 50,
              height: moduleToRender.height || 50,
              borderRadius: "50%",
              border: `2px solid ${moduleToRender.color || "#000000"}`,
              backgroundColor: moduleToRender.filled ? moduleToRender.color || "#000000" : "transparent",
            }}
          />
        );
        break;
      case "rectangular":
        content = (
          <div
            style={{
              width: moduleToRender.width || 100,
              height: moduleToRender.height || 50,
              border: `2px solid ${moduleToRender.color || "#000000"}`,
              backgroundColor: moduleToRender.filled ? moduleToRender.color || "#000000" : "transparent",
            }}
          />
        );
        break;
      default:
        return null;
    }

    // Calculate bounds based on PDF dimensions to prevent modules from going outside
    // Use scaled module dimensions for bounds calculation
    const scaledModuleWidth = moduleWidth * pdfScale;
    const scaledModuleHeight = moduleHeight * pdfScale;
    
    const bounds = pdfScaledSize.width > 0 && pdfScaledSize.height > 0 && scaledModuleWidth > 0 && scaledModuleHeight > 0
      ? {
          left: 0,
          top: 0,
          right: Math.max(0, pdfScaledSize.width - scaledModuleWidth),
          bottom: Math.max(0, pdfScaledSize.height - scaledModuleHeight),
        }
      : "parent";

    return (
      <Draggable
        key={module.id}
        position={{ x: scaledX, y: scaledY }}
        onStop={(e, data) => {
          e.stopPropagation();
          handleModuleMove(module.id, { x: data.x, y: data.y });
        }}
        onStart={(e) => e.stopPropagation()}
        onDrag={(e) => e.stopPropagation()}
        bounds={bounds}
      >
        <div
          data-module-id={module.id}
          style={style}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedModule(module);
          }}
          className="relative group"
        >
          {content}
          {selectedModule?.id === module.id && (
            <div className="absolute -top-8 left-0 flex space-x-1 bg-white rounded shadow-lg p-1">
              {module.module_type === "rectangular" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModuleConfig(module);
                  }}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                  title={t("drawings.module.color")}
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteModule(module.id);
                }}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
                title={t("drawings.module.delete")}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </Draggable>
    );
  };

  if (loading) {
    return <ResourceLoading />;
  }

  if (!drawing) {
    return <div>{t("drawings.load_error")}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={drawing.title}
        mainButton={{
          label: Object.keys(pendingChanges).length > 0 
            ? `${t("drawings.save_changes")} (${Object.keys(pendingChanges).length})`
            : t("Edit"),
          action: Object.keys(pendingChanges).length > 0 
            ? handleSaveAllChanges 
            : () => navigate(`/dashboard/drawings/${id}/edit`),
          disabled: saving,
          icon: PencilIcon,
        }}
        actions={[
          {
            label: t("Edit"),
            path: `/dashboard/drawings/${id}/edit`,
            icon: PencilIcon,
          },
        ]}
      />

      <div
        ref={containerRef}
        className="relative w-full h-[calc(100vh-80px)] overflow-hidden bg-gray-200"
        onContextMenu={handleContextMenu}
        onClick={() => setContextMenu(null)}
      >
        {/* PDF Container with Modules - Draggable for panning */}
        <Draggable
          position={pdfPosition}
          onStop={(e, data) => setPdfPosition({ x: data.x, y: data.y })}
          handle=".pdf-draggable-handle"
          cancel="[data-module-id]"
        >
          <div
            ref={pdfContainerRef}
            className="pdf-draggable-handle relative cursor-move"
            style={{
              zIndex: 1,
              width: pdfScaledSize.width > 0 ? `${pdfScaledSize.width}px` : 'auto',
              height: pdfScaledSize.height > 0 ? `${pdfScaledSize.height}px` : 'auto',
              display: 'inline-block',
              lineHeight: 0,
              margin: 0,
              padding: 0,
              boxSizing: 'border-box',
            }}
          >
            {pdfUrl && (
              <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages }) => {
                  setNumPages(numPages);
                  setPdfError(null);
                }}
                onLoadError={(error) => {
                  console.error("PDF load error:", error);
                  setPdfError(error.message || "Failed to load PDF file");
                  toast.error(t("drawings.load_error"));
                }}
                loading={<div className="p-4">Loading PDF...</div>}
              >
                <div style={{ lineHeight: 0, display: 'inline-block' }}>
                  <Page 
                    pageNumber={1} 
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    scale={pdfScale}
                    className="react-pdf__Page"
                    onLoadSuccess={(page) => {
                      // Get the base page dimensions (at scale 1.0)
                      const viewport = page.getViewport({ scale: 1.0 });
                      setPdfBaseSize({ width: viewport.width, height: viewport.height });
                    }}
                  />
                </div>
              </Document>
            )}
            {pdfError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700">
                <p className="font-semibold">Error loading PDF:</p>
                <p>{pdfError}</p>
                {drawing?.document?.id && (
                  <p className="text-sm mt-2">
                    Document ID: {drawing.document.id}
                  </p>
                )}
              </div>
            )}
            
            {/* Modules - positioned relative to PDF container */}
            {modules.map((module) => renderModule(module))}
          </div>
        </Draggable>

        {/* Context Menu */}
        {contextMenu && (
          <div
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[1001]"
            style={{
              left: `${contextMenu.clientX || (containerRef.current?.getBoundingClientRect().left + contextMenu.x)}px`,
              top: `${contextMenu.clientY || (containerRef.current?.getBoundingClientRect().top + contextMenu.y)}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => handleAddModule("arrow")}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              {t("drawings.module.arrow")}
            </button>
            <button
              onClick={() => handleAddModule("circle")}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center"
            >
              <CircleStackIcon className="h-5 w-5 mr-2" />
              {t("drawings.module.circle")}
            </button>
            <button
              onClick={() => handleAddModule("rectangular")}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center"
            >
              <Square3Stack3DIcon className="h-5 w-5 mr-2" />
              {t("drawings.module.rectangular")}
            </button>
          </div>
        )}

        {/* Zoom Controls - Fixed at bottom center */}
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg p-2 flex space-x-2 z-[1002]">
          <button
            onClick={() => {
              const newScale = Math.max(0.5, pdfScale - 0.1);
              setPdfScale(newScale);
              // Position will be recalculated by useEffect
            }}
            className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200"
          >
            -
          </button>
          <span className="px-3 py-1">{Math.round(pdfScale * 100)}%</span>
          <button
            onClick={() => {
              const newScale = Math.min(3, pdfScale + 0.1);
              setPdfScale(newScale);
              // Position will be recalculated by useEffect
            }}
            className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200"
          >
            +
          </button>
        </div>
      </div>

      {/* Module Configuration Dialog */}
      <PMDialog
        open={!!moduleConfigDialog}
        onClose={() => setModuleConfigDialog(null)}
        title={t("drawings.module.rectangular")}
      >
        {moduleConfigDialog && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("drawings.module.color")}
              </label>
              <input
                type="color"
                value={moduleConfigDialog.color || "#000000"}
                onChange={(e) =>
                  setModuleConfigDialog((prev) => ({
                    ...prev,
                    color: e.target.value,
                  }))
                }
                className="w-full h-10 rounded"
              />
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={moduleConfigDialog.filled || false}
                  onChange={(e) =>
                    setModuleConfigDialog((prev) => ({
                      ...prev,
                      filled: e.target.checked,
                    }))
                  }
                />
                <span>{t("drawings.module.filled")}</span>
              </label>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setModuleConfigDialog(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleSaveModuleConfig}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-400"
              >
                {saving ? t("Saving...") : t("Update")}
              </button>
            </div>
          </div>
        )}
      </PMDialog>
    </div>
  );
};

